import { useState, useEffect } from 'react';
import { isPushEnabled, waitForOneSignalInit } from '@/lib/services/onesignal';

export type PushNotificationStatus = 'enabled' | 'disabled' | 'loading';

/**
 * Hook to check the current push notification permission status
 * Returns 'loading' while checking, 'enabled' if granted, or 'disabled' if not
 *
 * This hook waits for OneSignal to initialize before checking permission
 * to avoid race conditions on app startup.
 */
export function usePushNotificationStatus(): PushNotificationStatus {
  const [status, setStatus] = useState<PushNotificationStatus>('loading');

  useEffect(() => {
    let cancelled = false;

    const checkStatus = async () => {
      // Wait for OneSignal to be initialized before checking permission
      const initialized = await waitForOneSignalInit();

      if (cancelled) return;

      if (!initialized) {
        // OneSignal not configured (e.g., missing app ID)
        // Treat as disabled so the banner doesn't show indefinitely
        setStatus('disabled');
        return;
      }

      const enabled = await isPushEnabled();
      if (!cancelled) {
        setStatus(enabled ? 'enabled' : 'disabled');
      }
    };

    checkStatus();

    return () => {
      cancelled = true;
    };
  }, []);

  return status;
}
